/**
 * Agent Configuration Component - Manage AI agents
 */

import { LitElement, html, css } from "lit";
import { customElement, state } from "lit/decorators.js";

// Local type definition for browser environment
interface AgentDefinition {
	id: string;
	name: string;
	description?: string;
	model: {
		provider: string;
		model: string;
	};
	skills: string[];
	session: {
		historyLimit: number;
		idleMinutes: number;
		dailyResetHour: number;
		compression: string;
		queuing: string;
	};
	bootstrap: {
		files: string[];
		maxChars: number;
	};
	memory: {
		enabled: boolean;
		files: string[];
	};
}

@customElement("piagnet-agent-config")
export class AgentConfig extends LitElement {
	@state() private agents: AgentDefinition[] = [];
	@state() private editingAgent?: AgentDefinition;
	@state() private showAddModal = false;

	static styles = css`
		:host {
			display: block;
			padding: 1.5rem;
		}

		.header {
			display: flex;
			justify-content: space-between;
			align-items: center;
			margin-bottom: 1.5rem;
		}

		.title {
			font-size: 1.5rem;
			font-weight: 600;
			color: var(--text-primary, #111827);
		}

		.btn {
			padding: 0.5rem 1rem;
			border-radius: 0.375rem;
			font-size: 0.875rem;
			font-weight: 500;
			cursor: pointer;
			border: none;
			transition: all 0.2s;
		}

		.btn-primary {
			background: var(--brand-color, #3b82f6);
			color: white;
		}

		.btn-primary:hover {
			background: var(--brand-color-dark, #2563eb);
		}

		.btn-danger {
			background: #fee2e2;
			color: #991b1b;
		}

		.btn-danger:hover {
			background: #fecaca;
		}

		.btn-secondary {
			background: var(--bg-secondary, #f3f4f6);
			color: var(--text-primary, #374151);
		}

		.agent-list {
			display: flex;
			flex-direction: column;
			gap: 1rem;
		}

		.agent-card {
			background: white;
			border: 1px solid var(--border-color, #e5e7eb);
			border-radius: 0.75rem;
			padding: 1.25rem;
			box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
		}

		.agent-header {
			display: flex;
			justify-content: space-between;
			align-items: flex-start;
			margin-bottom: 1rem;
		}

		.agent-name {
			font-size: 1.125rem;
			font-weight: 600;
			color: var(--text-primary, #111827);
		}

		.agent-description {
			font-size: 0.875rem;
			color: var(--text-secondary, #6b7280);
			margin-top: 0.25rem;
		}

		.agent-actions {
			display: flex;
			gap: 0.5rem;
		}

		.agent-details {
			display: grid;
			grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
			gap: 1rem;
			margin-top: 1rem;
			padding-top: 1rem;
			border-top: 1px solid var(--border-color, #e5e7eb);
		}

		.detail-item {
			display: flex;
			flex-direction: column;
			gap: 0.25rem;
		}

		.detail-label {
			font-size: 0.75rem;
			color: var(--text-secondary, #6b7280);
			text-transform: uppercase;
			letter-spacing: 0.05em;
		}

		.detail-value {
			font-size: 0.875rem;
			color: var(--text-primary, #111827);
			font-weight: 500;
		}

		.empty-state {
			text-align: center;
			padding: 3rem;
			color: var(--text-secondary, #6b7280);
		}

		.modal-overlay {
			position: fixed;
			inset: 0;
			background: rgba(0, 0, 0, 0.5);
			display: flex;
			align-items: center;
			justify-content: center;
			z-index: 100;
		}

		.modal {
			background: white;
			border-radius: 0.75rem;
			padding: 1.5rem;
			width: 100%;
			max-width: 500px;
			max-height: 90vh;
			overflow-y: auto;
		}

		.modal-title {
			font-size: 1.25rem;
			font-weight: 600;
			margin-bottom: 1rem;
		}

		.form-group {
			margin-bottom: 1rem;
		}

		.form-label {
			display: block;
			font-size: 0.875rem;
			font-weight: 500;
			margin-bottom: 0.5rem;
			color: var(--text-primary, #374151);
		}

		.form-input {
			width: 100%;
			padding: 0.5rem 0.75rem;
			border: 1px solid var(--border-color, #d1d5db);
			border-radius: 0.375rem;
			font-size: 0.875rem;
		}

		.form-input:focus {
			outline: none;
			border-color: var(--brand-color, #3b82f6);
			box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
		}

		.modal-actions {
			display: flex;
			justify-content: flex-end;
			gap: 0.75rem;
			margin-top: 1.5rem;
		}
	`;

	render() {
		return html`
			<div class="header">
				<h1 class="title">Agents</h1>
				<button class="btn btn-primary" @click=${() => this.showAddModal = true}>Add Agent</button>
			</div>

			${this.agents.length === 0
				? html`
					<div class="empty-state">
						<p>No agents configured</p>
						<p>Create your first agent to get started</p>
					</div>
				`
				: html`
					<div class="agent-list">
						${this.agents.map(agent => html`
							<div class="agent-card">
								<div class="agent-header">
									<div>
										<div class="agent-name">${agent.name}</div>
										${agent.description ? html`<div class="agent-description">${agent.description}</div>` : null}
									</div>
									<div class="agent-actions">
										<button class="btn btn-secondary" @click=${() => this.editAgent(agent)}>Edit</button>
										<button class="btn btn-danger" @click=${() => this.deleteAgent(agent.id)}>Delete</button>
									</div>
								</div>
								<div class="agent-details">
									<div class="detail-item">
										<div class="detail-label">Provider</div>
										<div class="detail-value">${agent.model.provider}</div>
									</div>
									<div class="detail-item">
										<div class="detail-label">Model</div>
										<div class="detail-value">${agent.model.model}</div>
									</div>
									<div class="detail-item">
										<div class="detail-label">Skills</div>
										<div class="detail-value">${agent.skills.join(", ") || "None"}</div>
									</div>
									<div class="detail-item">
										<div class="detail-label">History Limit</div>
										<div class="detail-value">${agent.session.historyLimit}</div>
									</div>
								</div>
							</div>
						`)}
					</div>
				`}

			${this.showAddModal ? this.renderModal() : null}
		`;
	}

	private renderModal() {
		return html`
			<div class="modal-overlay" @click=${() => this.showAddModal = false}>
				<div class="modal" @click=${(e: Event) => e.stopPropagation()}>
					<h2 class="modal-title">Add New Agent</h2>
					<form @submit=${this.handleSubmit}>
						<div class="form-group">
							<label class="form-label">Agent ID</label>
							<input type="text" class="form-input" name="id" placeholder="my-assistant" required>
						</div>
						<div class="form-group">
							<label class="form-label">Name</label>
							<input type="text" class="form-input" name="name" placeholder="My Assistant" required>
						</div>
						<div class="form-group">
							<label class="form-label">Description</label>
							<input type="text" class="form-input" name="description" placeholder="Optional description">
						</div>
						<div class="form-group">
							<label class="form-label">Model Provider</label>
							<select class="form-input" name="provider">
								<option value="anthropic">Anthropic</option>
								<option value="openai">OpenAI</option>
								<option value="google">Google</option>
							</select>
						</div>
						<div class="form-group">
							<label class="form-label">Model</label>
							<input type="text" class="form-input" name="model" placeholder="claude-3-5-sonnet-20241022" required>
						</div>
						<div class="modal-actions">
							<button type="button" class="btn btn-secondary" @click=${() => this.showAddModal = false}>Cancel</button>
							<button type="submit" class="btn btn-primary">Create Agent</button>
						</div>
					</form>
				</div>
			</div>
		`;
	}

	private async handleSubmit(e: SubmitEvent) {
		e.preventDefault();
		const form = e.target as HTMLFormElement;
		const formData = new FormData(form);

		const agent: AgentDefinition = {
			id: formData.get("id") as string,
			name: formData.get("name") as string,
			description: formData.get("description") as string || undefined,
			model: {
				provider: formData.get("provider") as string,
				model: formData.get("model") as string,
			},
			skills: [],
			session: {
				historyLimit: 100,
				idleMinutes: 60,
				dailyResetHour: 4,
				compression: "summary",
				queuing: "sequential",
			},
			bootstrap: {
				files: ["AGENTS.md", "SOUL.md", "USER.md", "IDENTITY.md", "TOOLS.md"],
				maxChars: 20000,
			},
			memory: {
				enabled: true,
				files: ["MEMORY.md"],
			},
		};

		this.agents = [...this.agents, agent];
		this.showAddModal = false;
		this.dispatchEvent(new CustomEvent("agents-changed", { detail: this.agents }));
	}

	private editAgent(agent: AgentDefinition) {
		this.editingAgent = agent;
	}

	private deleteAgent(id: string) {
		if (confirm("Are you sure you want to delete this agent?")) {
			this.agents = this.agents.filter(a => a.id !== id);
			this.dispatchEvent(new CustomEvent("agents-changed", { detail: this.agents }));
		}
	}
}
